import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Select,
  Tag,
  message,
  Spin,
  Space,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { CSVLink } from "react-csv";
import ManageCategoryTypeModal from "./ManageCategoryTypeModal";
import AddAssetModal from "./AddAssetModal";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { useSelector } from "react-redux";
import { getMenuList, getUserRoleId } from "redux/selectors";

const { Option } = Select;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  // margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;
const Stats = styled.div`
  display: flex;
  gap: 16px;
  font-size: 14px;
  color: #444;
  align-items: center;
  flex-wrap: wrap;
`;
const VerticalDivider = styled.div`
  width: 1px;
  height: 18px;
  background: #ddd;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;
const LinkBtn = styled.span`
  color: #2d53da;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`;
const HoverActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 0.15s ease;
`;

const CSV_HEADERS = [
  { label: "Category Name", key: "categoryname" },
  { label: "Category Type", key: "categorytype" },
  { label: "Models", key: "models" },
  { label: "Total Assets", key: "totalassets" },
  { label: "Assigned", key: "assigned" },
  { label: "Unassigned", key: "unassigned" },
  { label: "In Service", key: "inService" },
];

const CategoryView = ({ setDrillCategory, exportRef }) => {
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [form] = Form.useForm(); // add/edit modal form
  const [filterForm] = Form.useForm(); // toolbar filter form
  const [typeList, setTypeList] = useState([]);
  const [categoryListLoading, setCategoryListLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [assetCategory, setAssetCategory] = useState(null);
  const [csvData, setCsvData] = useState([]);

  const csvLinkRef = useRef(null);

  // Filter values via Form.useWatch — same pattern as HRPerformance
  const typeFilter = Form.useWatch("typeFilter", filterForm) || [];

  /* ── API ── */
  const getCategory = async () => {
    try {
      setLoading(true);
      const { categoryList, code } = await CALL_API(
        `/assets/categorytypes/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS)
        setData(Array.isArray(categoryList) ? categoryList : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryType = async () => {
    try {
      setCategoryListLoading(true);
      const { categoryList, code } = await CALL_API(
        `/assets/managecategories/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS)
        setTypeList(Array.isArray(categoryList) ? categoryList : []);
    } catch (err) {
      console.error(err);
    } finally {
      setCategoryListLoading(false);
    }
  };

  useEffect(() => {
    getCategory();
    getCategoryType();
  }, []);

  /* ── Register export trigger ── */
  useEffect(() => {
    if (exportRef) {
      exportRef.current = () => {
        if (!filtered.length) {
          message.error("No data available to download.");
          return;
        }
        setCsvData(
          filtered.map((r) => ({
            categoryname: r.categoryname || "",
            categorytype: r.categorytype || "",
            models: Array.isArray(r.model)
              ? r.model.join(" | ")
              : r.model || "",
            totalassets: r.totalassets ?? "0",
            assigned: r.assigned_count ?? "0",
            unassigned: r.available_count ?? "0",
            inService: r?.in_service_count ?? "0",
          }))
        );
        setTimeout(() => csvLinkRef.current?.link?.click());
      };
    }
  });

  /* ── Filtered ── */
  const filtered = data.filter((d) => {
    if (
      search &&
      !String(d?.categoryname || "")
        .toLowerCase()
        .includes(search.toLowerCase())
    ) {
      return false;
    }

    if (
      Array.isArray(typeFilter) &&
      typeFilter.length &&
      !typeFilter.includes(d?.categorytype)
    ) {
      return false;
    }

    return true;
  });

  /* ── CRUD ── */
  const handleEdit = (record) => {
    setIsEdit(true);
    setSelectedCategory(record);
    form.setFieldsValue({
      categoryName: record?.categoryname,
      categoryType: record?.categorytype,
      models: record?.model,
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setIsEdit(false);
    setSelectedCategory(null);
    setModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      setAddLoading(true);
      const values = await form.validateFields();
      if (!values?.models?.length) {
        message.error("Please add at least one model");
        return;
      }
      const findmasterid = typeList?.find(
        (item) => item?.categorytype === values.categoryType
      )?.masterid;
      const payload = {
        categoryname: values.categoryName,
        categorytype: values.categoryType,
        model: values.models,
        masterid: findmasterid,
      };
      const { code } = isEdit
        ? await CALL_API(
            `/assets/categorytypes/${selectedCategory.categoryid}`,
            "patch",
            payload
          )
        : await CALL_API("/assets/categorytypes", "post", payload);

      // Backend found the same name (e.g. added from another tab/run after this
      // page loaded): keep the modal open, show the error, refresh the list
      if (code === STATUS_CODE.RECORD_EXIST) {
        form.setFields([
          {
            name: "categoryName",
            errors: ["This category name already exists"],
          },
        ]);
        getCategory();
        return;
      }
      if (code === STATUS_CODE.SUCCESS)
        message.success(
          isEdit ? "Category updated successfully" : "Category added successfully"
        );
      handleClose();
      getCategory();
      getCategoryType();
    } catch (err) {
      console.error(err);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete Category",
      content: "Are you sure you want to delete this category?",
      okText: "Delete",
      cancelText: "Cancel",
      centered: true,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setLoading(true);
          const { code } = await CALL_API(
            `/assets/categorytypes/${record?.categoryid}`,
            "delete"
          );
          if (code === STATUS_CODE.SUCCESS) {
            message.success("Category deleted successfully");
            getCategory();
          } else {
            message.error("Failed to delete category");
            setLoading(false);
          }
        } catch {
          message.error("Failed to delete category");
          setLoading(false);
        }
      },
    });
  };

  const uniqueTypes = [
    ...new Set(data.map((d) => d.categorytype).filter(Boolean)),
  ];

  const columns = [
    { title: "Category Name", dataIndex: "categoryname", key: "categoryname" },
    {
      title: "Category Type",
      dataIndex: "categorytype",
      key: "categorytype",
      render: (v) => <span style={{ color: "#888" }}>{v}</span>,
    },
    // {
    //   title: "Models",
    //   dataIndex: "model",
    //   key: "model",
    //   render: (ms) =>
    //     ms?.length ? (
    //       ms.map((m) => (
    //         <Tag key={m} style={{ borderRadius: 4 }}>
    //           {m}
    //         </Tag>
    //       ))
    //     ) : (
    //       <span style={{ color: "#bbb" }}>---</span>
    //     ),
    // },
    {
      title: "Total Asset",
      dataIndex: "totalassets",
      key: "totalassets",
      render: (total) => {
        return total ? total : "0";
      },
    },
    { title: "Assigned", dataIndex: "assigned_count", key: "assigned_count" },
    {
      title: "Unassigned",
      dataIndex: "available_count",
      key: "available_count",
    },
    {
      title: "In Service",
      dataIndex: "in_service_count",
      key: "in_service_count",
    },
    {
      title: "",
      key: "actions",
      width: 220,
      render: (_, record) => (
        <HoverActions
          $visible={
            hoveredKey === record.categoryid &&
            record.categorystatus !== "DEFAULT"
          }
        >
          <Space size={20}>
            {Menulist?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "assets" && subItem?.permission?.create
                )
            ) && (
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => {
                  setAssetCategory(record);
                  setAddAssetOpen(true);
                }}
                style={{ padding: 0, color: "#2D53DA" }}
              >
                Add Asset
              </Button>
            )}
            {Menulist?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "assets" && subItem?.permission?.edit
                )
            ) && (
              <EditOutlined
                onClick={() => handleEdit(record)}
                style={{ color: "#2D53DA", cursor: "pointer", fontSize: 16 }}
              />
            )}
            {record?.totalassets === 0 &&
              Menulist?.find(
                (item) =>
                  item?.key === "HRResources" &&
                  item?.subMenu?.find(
                    (subItem) =>
                      subItem?.key === "assets" && subItem?.permission?.delete
                  )
              ) && (
                <DeleteOutlined
                  onClick={() => handleDelete(record)}
                  style={{ color: "#2D53DA", cursor: "pointer", fontSize: 16 }}
                />
              )}
          </Space>
        </HoverActions>
      ),
    },
  ];

  return (
    <div>
      {/* Hidden CSV link */}
      <CSVLink
        headers={CSV_HEADERS}
        data={csvData}
        filename="category_view.csv"
        ref={csvLinkRef}
        style={{ display: "none" }}
      />

      <Toolbar>
        <Stats>
          <span>
            Total Category: <strong>{data?.length || 0}</strong>
          </span>
          <VerticalDivider />
          <span>
            Total Asset:{" "}
            <strong>
              {data.reduce((a, b) => a + (b.totalassets || 0), 0)}
            </strong>
          </span>
        </Stats>
        <Actions>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 150, borderRadius: 20 }}
          />
          <Form form={filterForm} layout="inline" style={{ marginBottom: 0 }}>
            <Form.Item
              name="typeFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="typeFilter"
                label="Category Type"
                form={filterForm}
                options={uniqueTypes.map((t) => ({ label: t, value: t }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Form>
          <LinkBtn onClick={() => setManageOpen(true)}>
            Manage Category Type
          </LinkBtn>
          <VerticalDivider />
          {Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.create
              )
          ) && (
            <LinkBtn onClick={() => setModalOpen(true)}>Add Category</LinkBtn>
          )}
        </Actions>
      </Toolbar>

      <Spin spinning={loading}>
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={filtered}
            pagination={false}
            size="middle"
            rowKey="categoryid"
            onRow={(record) => ({
              onClick: (e) => {
                if (
                  e.target.closest(
                    "button, .ant-btn, .anticon-delete, .anticon-edit"
                  )
                )
                  return;
                setDrillCategory(record);
              },
              onMouseEnter: () => setHoveredKey(record.categoryid),
              onMouseLeave: () => setHoveredKey(null),
              style: { cursor: "pointer" },
            })}
            style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
          />
        </div>
      </Spin>

      <AddAssetModal
        open={addAssetOpen}
        onClose={() => {
          setAddAssetOpen(false);
          setAssetCategory(null);
        }}
        categoryList={data}
        preselectedCategory={assetCategory}
        onSuccess={getCategory}
      />

      <ManageCategoryTypeModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        typeList={typeList}
        setTypeList={setTypeList}
        getCategoryType={getCategoryType}
        categoryListLoading={categoryListLoading}
      />

      <Modal
        title={isEdit ? "Editing Category" : "Adding Category"}
        open={modalOpen}
        onCancel={handleClose}
        centered
        width={460}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={addLoading}
            onClick={handleSubmit}
          >
            {isEdit ? "Update" : "Add"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Category Name"
            name="categoryName"
            rules={[
              {
                required: true,
                message: "Category name is required",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const duplicate = data.some((item) => {
                    // Ignore current record while editing
                    if (
                      isEdit &&
                      item.categoryid === selectedCategory?.categoryid
                    ) {
                      return false;
                    }

                    return (
                      item.categoryname?.trim().toLowerCase() ===
                      value.trim().toLowerCase()
                    );
                  });

                  if (duplicate) {
                    return Promise.reject(
                      new Error("This category name already exists")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="e.g. Laptop" style={{ borderRadius: 6 }} />
          </Form.Item>

          <Form.Item
            label="Category Type"
            name="categoryType"
            rules={[
              { required: true, message: "Please select a category type" },
            ]}
          >
            <Select placeholder="Select type" style={{ width: "100%" }}>
              {typeList.map((t) => (
                <Option key={t?.masterid} value={t?.categorytype}>
                  {t?.categorytype}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Model"
            name="models"
            rules={[
              { required: true, message: "Please add at least one model" },
            ]}
          >
            <Select
              mode="tags"
              placeholder="Type model and press Enter"
              open={false}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryView;
